/**
 * Auto-assignment proposal for sessions.
 */
import * as React from 'react';
import { Sparkles, AlertTriangle, UserCircle2 } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { AUTO_ASSIGNMENT_SOURCE_LABELS } from '@/components/sessions/session-configs';
import {
  formatAutoAssignmentFooterLabel,
  formatProposalUserName,
  groupProposalPairsByTutor,
  proposalPairsToMapping,
} from '@/components/sessions/inperson/session-form-utils';
import type { AutoAssignmentProposal } from '@/types/inperson/matching.types';
import type { UserWithProfile } from '@/types/user.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link AutoAssignmentProposalModal}. */
interface AutoAssignmentProposalModalProps {
  open: boolean;
  onClose: () => void;
  /** Apply the suggestion: pass the student → tutor mapping to the parent. */
  onApply: (studentToTutor: Record<string, string>) => void;
  proposal: AutoAssignmentProposal | null;
  tutors: UserWithProfile[];
  students: UserWithProfile[];
  isLoading?: boolean;
}

/** Auto-assignment proposal for sessions. */
export const AutoAssignmentProposalModal: React.FC<AutoAssignmentProposalModalProps> = ({
  open,
  onClose,
  onApply,
  proposal,
  tutors,
  students,
  isLoading = false,
}) => {
  const studentById = React.useMemo(() => new Map(students.map((s) => [s.id, s])), [students]);

  /** Proposed pairs grouped under each tutor for the preview list. */
  const groupsByTutor = React.useMemo(
    () => groupProposalPairsByTutor(tutors, proposal?.proposedPairs),
    [proposal, tutors],
  );

  const unassigned = proposal?.unassignedStudentIds ?? [];
  const totalAssigned = proposal?.proposedPairs.length ?? 0;

  /** Flatten proposed pairs into `studentId → tutorId` and hand them to the parent. */
  const handleApply = () => {
    if (!proposal) return;
    onApply(proposalPairsToMapping(proposal.proposedPairs));
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size="2xl"
      header={{
        icon: Sparkles,
        iconSize: 'md',
        title: 'Suggestion d’affectation automatique',
        subtitle:
          'Ceci est une suggestion. Vous pouvez l’appliquer puis l’ajuster avant de créer la séance.',
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      hideContentWrapper
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            {formatAutoAssignmentFooterLabel(isLoading, totalAssigned, unassigned.length)}
          </span>
          <div className="flex gap-2">
            <Button preset="modal-cancel" onClick={onClose} disabled={isLoading}>
              Annuler
            </Button>
            <Button
              onClick={handleApply}
              disabled={isLoading || !proposal || proposal.proposedPairs.length === 0}
            >
              Appliquer cette suggestion
            </Button>
          </div>
        </div>
      }
    >
      <div className="mt-2 space-y-4">
        {isLoading && (
          <div
            className="rounded-2xl border-2 border-dashed p-6 text-center text-sm"
            style={{
              borderColor: 'var(--color-1-nuance-3)',
              color: 'var(--muted-foreground)',
            }}
          >
            Recherche d’une affectation optimale…
          </div>
        )}

        {!isLoading && proposal?.proposedPairs.length === 0 && (
          <div
            className="rounded-2xl border-2 p-5 text-sm"
            style={{
              borderColor: 'var(--color-1-nuance-3)',
              background: 'color-mix(in srgb, var(--color-1-nuance-3) 4%, transparent)',
              color: 'var(--muted-foreground)',
            }}
          >
            Aucun binôme n’a pu être proposé pour cette configuration. Vérifiez que les tuteurs et
            élèves sélectionnés sont compatibles (établissement, disponibilité…).
          </div>
        )}

        {!isLoading && proposal && proposal.proposedPairs.length > 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {groupsByTutor.map(({ tutor, pairs }) => (
              <div
                key={tutor.id}
                className="rounded-2xl border-2 p-4"
                style={{
                  borderColor: 'var(--color-1-nuance-3)',
                  background: 'color-mix(in srgb, var(--color-1-nuance-3) 3%, transparent)',
                }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-lg"
                    style={{
                      background: 'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)',
                    }}
                  >
                    <UserCircle2 className="h-5 w-5" style={{ color: 'var(--color-2-nuance-1)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-semibold">
                      {formatProposalUserName(tutor)}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {pairs.length} élève{pairs.length > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>

                {pairs.length === 0 ? (
                  <p className="mt-3 text-xs italic" style={{ color: 'var(--muted-foreground)' }}>
                    Aucun élève proposé pour ce tuteur.
                  </p>
                ) : (
                  <ul className="mt-3 space-y-1.5">
                    {pairs.map((p) => {
                      const student = studentById.get(p.studentId);
                      const meta = AUTO_ASSIGNMENT_SOURCE_LABELS[p.source];
                      const SourceIcon = meta.icon;
                      return (
                        <li
                          key={p.studentId}
                          className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5"
                          style={{
                            background: 'color-mix(in srgb, var(--background) 60%, transparent)',
                          }}
                        >
                          <span className="truncate text-sm">
                            {formatProposalUserName(student)}
                          </span>
                          <span
                            className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                            style={{
                              color: meta.color,
                              background: `color-mix(in srgb, ${meta.color} 12%, transparent)`,
                            }}
                            title={meta.label}
                          >
                            <SourceIcon className="h-3 w-3" />
                            {meta.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {!isLoading && unassigned.length > 0 && (
          <div
            className="rounded-2xl border-2 p-4"
            style={{
              borderColor: 'var(--accent-orange, #f59e0b)',
              background: 'color-mix(in srgb, var(--accent-orange, #f59e0b) 8%, transparent)',
            }}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle
                className="h-4 w-4"
                style={{ color: 'var(--accent-orange, #f59e0b)' }}
              />
              <span className="text-sm font-medium">
                {getErrorMessage({
                  code: 'autoAssignUnassignedCount',
                  count: unassigned.length,
                })}
              </span>
            </div>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {unassigned.map((sid) => (
                <li
                  key={sid}
                  className="rounded-full px-2 py-0.5 text-xs"
                  style={{
                    background:
                      'color-mix(in srgb, var(--accent-orange, #f59e0b) 14%, transparent)',
                    color: 'var(--accent-orange, #f59e0b)',
                  }}
                >
                  {formatProposalUserName(studentById.get(sid))}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Vous pourrez les affecter manuellement après avoir appliqué la suggestion.
            </p>
          </div>
        )}
      </div>
    </BaseModal>
  );
};
